import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Paper, useTheme, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, ThemeProvider, CssBaseline, Backdrop, CircularProgress } from "@mui/material";
import { Add, Cancel, Delete, Edit, Remove, Save, Visibility } from "@mui/icons-material";
import { CREATE_DATASETS_URL, CREATE_TESTSUITE_URL, DELETE_DATASETS_URL, DELETE_TESTSUITE_URL, GET_ALL_DATASETS_URL, GET_ALL_TESTSUITES_BY_TENANTID_URL, UPDATE_DATASET_URL, UPDATE_TESTSUITE_URL } from "envConfig";
import { Logger } from "utils/Logger";
import { DataSet, TestSuite as TestSuite_Structure } from "../../Interfaces/AutomateTest";
import { useSelector } from "react-redux";
import { RootState } from "state/store";

export const TestSuite: React.FC = () => {
    const theme = useTheme();
    const { tenantId } = useParams<{ tenantId: string }>();
    const [testSuites, setTestSuites] = useState<TestSuite_Structure[]>([]);
    const [dataSets, setDataSets] = useState<DataSet[]>([]);
    const [editMode, setEditMode] = useState<string | null>(null);
    const [testSuiteEditedRow, setTestSuiteEditedRow] = useState<Partial<TestSuite_Structure>>({});
    const [newTestSuiteRow, setnewTestSuiteRow] = useState<TestSuite_Structure | null>(null);
    const [nameError, setNameError] = useState(false);
    const [currentDataSet, setCurrentDataSet] = useState<Partial<DataSet> | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();

    const user = useSelector((state: RootState) => state.authentication.user);

  // Fetch TestSuites from the API
    const fetchTestSuites = async () => {
      setLoading(true);
      try {
        const response = await fetch(GET_ALL_TESTSUITES_BY_TENANTID_URL+`/${tenantId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
  
        const data = await response.json();
        if (Array.isArray(data.data)) {
            setTestSuites(data.data);
            setLoading(false);
            Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : fetchTestSuites(): ", "testSuites:"+ JSON.stringify(data.data, null, 2));
        } else {
          setTestSuites([]);
          setLoading(false);
          Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : fetchTestSuites(): Expected an array, received:", JSON.stringify(data.data));
        }

      } catch (error) {
          setTestSuites([]);
          setLoading(false);
          Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : fetchTestSuites(): ", "Failed to fetch testSuites:"+ error);
      }
    };
    useEffect(() => {
      fetchTestSuites();
    }, []);

const handleAddNewTestSuiteRow = () => {
    const uniqueTestSuiteIds = [...new Set(testSuites.map((ts) => ts.testSuiteId))];
    const maxId = Math.max(...uniqueTestSuiteIds.map((id) => parseInt(id, 10) || 0));
    let newTestSuiteId= "1";
    if (uniqueTestSuiteIds.length != 0) {
       newTestSuiteId= (maxId + 1).toString();
    }
    

    const newTestSuite: TestSuite_Structure = {
      _id: "",
      testSuiteId: newTestSuiteId,
      name: "",
      userId: user._id,
      companyId: user.companyId,
      versionNumber: 1.0,
      tenantId: tenantId as string,
      description: "",
      status: "Draft",
      testCases: [],
      dataSets: [],
    };
    setnewTestSuiteRow(newTestSuite);
  };

  const openDialog = (dataSet?: DataSet) => {
    setCurrentDataSet(
      dataSet || {
      dataSetId: dataSets.reduce((max, ts) => Math.max(max, ts.dataSetId), 0) + 1,
      name: "",
      description: "",
      data: []
      }
    );
    setIsEditing(!!dataSet);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentDataSet(null);
  };

  const saveDataSet = async () => {
    if (!currentDataSet) return;
    try {
      setLoading(false);
      const url = isEditing
        ? `${UPDATE_DATASET_URL}/${tenantId}/${currentDataSet._id}`
        : CREATE_DATASETS_URL+`/${tenantId}`;
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(currentDataSet),
      });

      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.statusText}`);
      }
      await fetchDataSets();
      setLoading(false);
      closeDialog();
    } catch (error) {
      setLoading(false);
      Logger.error("Failed to save  dataSet:", error);
    }
  };

  const handleTestSuiteEdit = (_id: string) => {
    setEditMode(_id);
    const suite = testSuites.find((suite) => suite._id === _id);
    if (suite) setTestSuiteEditedRow(suite);
  };

  const handleCancelEdit = () => {
    // setnewDataSetRow(null);
    setnewTestSuiteRow(null);
    setEditMode(null);
    setTestSuiteEditedRow({});
  };

  const handleCreateNewTestSuite = async () => {
    if (newTestSuiteRow) {
      try {
        if (!newTestSuiteRow.name) {
          setNameError(true); // Set error if name is empty
          return;
        }
        setLoading(true);
        const response = await fetch(CREATE_TESTSUITE_URL+`/${tenantId}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(newTestSuiteRow),
        });

        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchTestSuites();
        //setTestSuites([...testSuites, newTestSuiteRow]);
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : handleSaveNewTestSuite(): ", "Failed to save new TestSuite: "+ JSON.stringify(setTestSuites, null, 2));

        setnewTestSuiteRow(null);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : handleSaveNewTestSuite(): ", "Failed to save new TestSuite: "+ error);
      }
    }
  };

  const handleUpdateTestSuite = async (_id:string) => {
    try {
      setLoading(true);
      const response = await fetch(`${UPDATE_TESTSUITE_URL}/${_id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(testSuiteEditedRow),
      });

      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.statusText}`);
      }

      setTestSuites((prev) =>
        prev.map((suite) =>
          suite._id === testSuiteEditedRow._id ? { ...suite, ...testSuiteEditedRow } : suite
        )
      );
      setEditMode(null);
      setTestSuiteEditedRow({});
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Logger.error("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : handleSave(): ", "Failed to save testSuite:" + error);
    }
  };

  const handleTestSuiteInputChange = (_id: string, field: keyof TestSuite_Structure, value: string) => {
    if (_id === "" && newTestSuiteRow) {
      setnewTestSuiteRow((prev) => prev ? { ...prev, [field]: value } : null);
      if (field === "name" && value) {
        setNameError(false);
      }
    } else if (editMode === _id) {
      setTestSuiteEditedRow((prev) => ({ ...prev, [field]: value }));
    } else {
      setTestSuites((prev) =>
        prev.map((testSuite) =>
          testSuite._id === _id ? { ...testSuite, [field]: value } : testSuite
        )
      );
    }
  };

  const handleDeleteTestSuite = async(_id: string) => {
    try {
      setLoading(true);
        const response = await fetch(DELETE_TESTSUITE_URL+`/${_id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchTestSuites();
      } catch (error) {
        setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : handleDeleteTestSuite(): ", "Failed to delete testSuite:"+ error);
      }
  };

    const fetchDataSets = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${GET_ALL_DATASETS_URL}/${tenantId}`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
  
        const data = await response.json();
        if (Array.isArray(data.data)) {
            setDataSets(data.data);
            setLoading(false);
            Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : fetchDataSets(): ", `DataSets: ${data.data.length} items`);
        } else {
          setDataSets([]);
          setLoading(false);
          Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : fetchDataSets(): Expected an array, received:", data.data);
        }

      } catch (error) {
        setDataSets([]);
        setLoading(false);
          Logger.debug("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : fetchDataSets(): ", "Failed to fetch DataSets:"+ error);
      }
    };
    useEffect(() => {
      fetchDataSets();
    }, []);

  const handleDeleteDataSets = async(dataSet_Id:string) => {
    try {
      setLoading(true);
        const response = await fetch(DELETE_DATASETS_URL+`/${tenantId}/${dataSet_Id}`, {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
        await fetchDataSets();
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Logger.error("st-ck-client/src/modules/AutomateTest/TestSuite/TestSuite.ts : handleDeleteDataSets(): ", "Failed to delete testSuite:"+ error);
      }
  };


  const handleAddFieldSet = () => {
    setCurrentDataSet((prev) => ({
      ...prev,
      data: [...(prev?.data || []), { key: "", value: "", elementKeyTypeFromDataSet: "" }],
    }));
  };

  const handleRemoveFieldSet = (index:number) => {
    setCurrentDataSet((prev) => ({
      ...prev,
      data: prev?.data?.filter((_, i) => i !== index),
    }));
  };

  const handleFieldChange = (index:number, field:string, value:string) => {
    setCurrentDataSet((prev) => ({
      ...prev,
      data: prev?.data?.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  return (loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1}  width="100%">
        <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" gutterBottom>
        All TestSuits
      </Typography>
      <Box mb={2}>
        <Button variant="contained" sx={{ backgroundColor: theme.palette.primary.main }} startIcon={<Add />} onClick={handleAddNewTestSuiteRow}>
          New Suite
        </Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>NAME</TableCell>
              <TableCell>DESCRIPTION</TableCell>
              <TableCell>TEST CASES</TableCell>
              <TableCell sx={{display: "flex", justifyContent: "flex-end"}}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testSuites.map((testSuite) => (
              <TableRow key={testSuite._id}>
                <TableCell>{testSuite.testSuiteId +"["+ testSuite.versionNumber+"]"}</TableCell>
                <TableCell>
                  {editMode === testSuite._id ? (
                    <TextField size="small" label="Name"
                      value={testSuiteEditedRow.name || ""}
                      onChange={(e) => handleTestSuiteInputChange(testSuite._id, "name", e.target.value)} error={nameError}
                      helperText={nameError ? "Name is required" : ""}
                    />
                  ) : (
                    testSuite.name
                  )}
                </TableCell>
                <TableCell>
                  {editMode === testSuite._id ? (
                    <TextField size="small" label="Description"
                      value={testSuiteEditedRow.description || ""}
                      onChange={(e) => handleTestSuiteInputChange(testSuite._id, "description", e.target.value)}
                    />
                  ) : (
                    testSuite.description
                  )}
                </TableCell>
                <TableCell>{testSuite.testCases?.length || 0}</TableCell>
                <TableCell sx={{display: "flex", justifyContent: "flex-end"}}>
                  {editMode === testSuite._id ? (
                    <>
                      <IconButton onClick={()=>handleUpdateTestSuite(testSuite._id)}>
                        <Save />
                      </IconButton>
                      <IconButton onClick={handleCancelEdit}>
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => navigate(`/tenant/${tenantId}/testSuite/${testSuite.testSuiteId}`)}>
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => handleTestSuiteEdit(testSuite._id)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteTestSuite(testSuite._id)}>
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
              {newTestSuiteRow && (
              <TableRow>
                <TableCell>New</TableCell>
                <TableCell>
                  <TextField size="small" label="Name"
                    value={newTestSuiteRow.name}
                    onChange={(e) => handleTestSuiteInputChange("", "name", e.target.value)} error={nameError}
                    helperText={nameError ? "Name is required" : ""}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" label="Description"
                    value={newTestSuiteRow.description}
                    onChange={(e) => handleTestSuiteInputChange("", "description", e.target.value)}
                  />
                </TableCell>
                <TableCell>{"0"}</TableCell>
                <TableCell>
                <TableCell>
                  <IconButton onClick={handleCreateNewTestSuite}>
                    <Save />
                  </IconButton>
                  <IconButton onClick={handleCancelEdit}>
                    <Cancel />
                  </IconButton>
                </TableCell>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={3} mb={2} display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h4" gutterBottom>
        DataSets
      </Typography>
        <Button  variant="contained"
          sx={{ backgroundColor: theme.palette.primary.main }}
          startIcon={<Add />}
          onClick={() => openDialog()}>
          Add Data Set
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>NAME</TableCell>
              <TableCell>DESCRIPTION</TableCell>
              <TableCell>DATA</TableCell>
              <TableCell sx={{display: "flex", justifyContent: "flex-end"}}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataSets.map((dataSet) => (
              <TableRow key={dataSet._id}>
                <TableCell>{dataSet.dataSetId}</TableCell>
                <TableCell>{dataSet.name}</TableCell>
                <TableCell>{dataSet.description}</TableCell>
                <TableCell>{dataSet.data.map((item,index) => (<Box display="flex" justifyContent="space-between" alignItems="center"
                 key={index}> <p>Key = {item.key} | Value = {item.value} | Type = {item.elementKeyTypeFromDataSet}</p></Box>))}
                 </TableCell>
                <TableCell sx={{display: "flex", justifyContent: "flex-end"}}>
                  {editMode === dataSet._id ? (
                    <>
                      <IconButton onClick={saveDataSet}>
                        <Save />
                      </IconButton>
                      <IconButton onClick={handleCancelEdit}>
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        onClick={() =>
                          navigate(`/tenant/${tenantId}/dataSet/${dataSet.dataSetId}`)
                        }
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton onClick={() => openDialog(dataSet)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteDataSets(dataSet._id)}>
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for Add/Edit */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth>
        <DialogTitle>{isEditing ? "Edit DataSet" : "Add New DataSet"}</DialogTitle>
        <DialogContent>
          {Object.keys(currentDataSet || {}).filter((key) => key !== "_id").map((key) =>
            key !== "data" ? (
              <TextField
                size="small"
                key={key}
                label={key}
                value={(currentDataSet || {})[key as keyof DataSet] || ""}
                onChange={(e) =>
                  setCurrentDataSet((prev) =>
                    prev ? { ...prev, [key]: e.target.value } : null
                  )
                }
                fullWidth
                margin="normal"
                disabled={key === "dataSetId"}
                required={key === "name"}
              />
            ) : (
              <Box key={key}>
                <p>Data</p>
                {(currentDataSet?.data || []).map((fieldSet, index) => (
                  <Box key={index} display="flex" gap={2} alignItems="center" mb={2}>
                    <TextField
                      size="small"
                      label="Key"
                      value={fieldSet.key}
                      onChange={(e) =>
                        handleFieldChange(index, "key", e.target.value)
                      }
                    />
                    <TextField
                      size="small"
                      label="Value"
                      value={fieldSet.value}
                      onChange={(e) =>
                        handleFieldChange(index, "value", e.target.value)
                      }
                    />
                    <TextField
                      size="small"
                      label="keyType"
                      value={fieldSet.elementKeyTypeFromDataSet}
                      onChange={(e) =>
                        handleFieldChange(index, "elementKeyTypeFromDataSet", e.target.value)
                      }
                    />
                    <IconButton onClick={() => handleRemoveFieldSet(index)}>
                      <Remove />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  sx={{ backgroundColor: "primary.light" }}
                  startIcon={<Add />}
                  onClick={handleAddFieldSet}
                  variant="outlined"
                >
                  Add Data
                </Button>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ backgroundColor: "primary.light" }}
            onClick={closeDialog}
            startIcon={<Cancel />}
          >
            Cancel
          </Button>
          <Button
            onClick={saveDataSet}
            startIcon={<Save />}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
